import 'leaflet/dist/leaflet.css';
import type { TreeWell } from '@twinlink/shared';
import { latLngBounds, type LatLngBounds } from 'leaflet';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleMarker, MapContainer, TileLayer, Tooltip, useMap } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import { STATUS_COLOR } from './AssetTreeView';
import classes from './AssetsPage.module.css';

// OpenStreetMap by default; the tile URL is configurable (doc 01 §2).
const TILE_URL = (import.meta.env.VITE_TILE_URL as string | undefined) ?? 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTRIBUTION = (import.meta.env.VITE_TILE_ATTRIBUTION as string | undefined) ?? '&copy; OpenStreetMap contributors';

/** Leaflet measures its container once: when the box is sized later (a dashboard widget), measure again and re-frame the wells. */
function FitOnResize({ bounds }: { bounds: LatLngBounds }) {
  const map = useMap();
  const key = bounds.toBBoxString();
  useEffect(() => {
    const observer = new ResizeObserver(() => {
      map.invalidateSize();
      map.fitBounds(bounds);
    });
    observer.observe(map.getContainer());
    return () => observer.disconnect();
    // The bounds object is rebuilt on every render; its text form says whether it changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, key]);
  return null;
}

/** Wells in scope as status-coloured dots; the status is also in the tooltip text. */
export function WellMap({ wells, hrefFor, height }: { wells: Array<TreeWell & { fieldName?: string }>; hrefFor: (id: string) => string; height?: number }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const located = wells.filter((s) => s.location);
  if (located.length === 0) return <div className={classes.mapEmpty}>{t('wellsites.mapEmpty')}</div>;
  const bounds = latLngBounds(located.map((s) => [s.location!.lat, s.location!.lng] as [number, number])).pad(0.4);
  return (
    <MapContainer bounds={bounds} maxZoom={15} scrollWheelZoom={false} className={classes.map} style={height ? { height } : undefined} attributionControl>
      <TileLayer url={TILE_URL} attribution={TILE_ATTRIBUTION} />
      <FitOnResize bounds={bounds} />
      {located.map((s) => (
        <CircleMarker
          key={s.id}
          center={[s.location!.lat, s.location!.lng]}
          radius={9}
          pathOptions={{ color: '#ffffff', weight: 2, fillColor: STATUS_COLOR[s.status], fillOpacity: 1 }}
          eventHandlers={{ click: () => navigate(hrefFor(s.id)) }}
        >
          <Tooltip direction="top" offset={[0, -8]}>
            <strong>{s.name}</strong> — {t(`wellsites.status.${s.status}`)}
            {s.fieldName && (
              <>
                <br />
                {s.fieldName}
              </>
            )}
            {s.runningTest && (
              <>
                <br />
                {s.runningTest.name}
              </>
            )}
          </Tooltip>
        </CircleMarker>
      ))}
    </MapContainer>
  );
}
